// src/components/cards/PortalCard.tsx
// Shared card used in both BusinessSelectionPage and landlord property lists.
// Matches LandlordPropertyCard exactly: shadow depth, bg tokens, divider, icon square.
import React from 'react';
import { LucideIcon } from 'lucide-react';

interface StatItem {
  icon: LucideIcon;
  label: string;
}

interface PortalCardProps {
  title: string;
  subtitle?: string;
  icon: LucideIcon;
  iconColor?: string;       // tailwind text colour class e.g. "text-primary"
  iconBg?: string;          // tailwind bg colour class e.g. "bg-primary/10"
  badge?: React.ReactNode;  // e.g. Active / Inactive pill
  stats?: StatItem[];
  onClick?: () => void;
}

export const PortalCard: React.FC<PortalCardProps> = ({
  title,
  subtitle,
  icon: Icon,
  iconColor = 'text-light-subtle dark:text-dark-subtle',
  iconBg = 'bg-light-bg dark:bg-dark-bg',
  badge,
  stats = [],
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="text-left w-full bg-light-card dark:bg-dark-card rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-150 p-4 flex flex-col justify-between"
  >
    {/* Top row: title + icon */}
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1 min-w-0">
        <p className="text-base font-semibold text-light-text dark:text-dark-text truncate">
          {title}
        </p>
        {subtitle && (
          <p className="text-xs text-light-subtle dark:text-dark-subtle mt-1 truncate">
            {subtitle}
          </p>
        )}
        {badge && <div className="mt-2">{badge}</div>}
      </div>
      <div className={`p-2 rounded-lg flex-shrink-0 ${iconBg} ${iconColor}`}>
        <Icon size={20} />
      </div>
    </div>

    {/* Divider */}
    {stats.length > 0 && (
      <div className="my-3 border-t border-light-border/50 dark:border-dark-border/50" />
    )}

    {/* Stats row */}
    {stats.length > 0 && (
      <div className="flex items-center gap-2 flex-wrap">
        {stats.map((s, i) => (
          <React.Fragment key={i}>
            {i > 0 && (
              <span className="text-light-subtle dark:text-dark-subtle text-xs">•</span>
            )}
            <s.icon size={14} className="text-light-subtle dark:text-dark-subtle flex-shrink-0" />
            <span className="text-xs text-light-subtle dark:text-dark-subtle">{s.label}</span>
          </React.Fragment>
        ))}
      </div>
    )}
  </button>
);
